import clsx from 'clsx'
import {
  AlertTriangle, AtSign, Bell, CalendarClock, CalendarDays, CalendarOff, CalendarX, CheckSquare, Clock, FileCheck, MessageSquare, Package,
  StickyNote, type LucideIcon,
} from 'lucide-react'
import type { AppNotification } from '../../api/notifications'
import { formatDateTime } from '../../lib/format'

/** Ikona a barva podle typu notifikace. */
function iconFor(type: string): { Icon: LucideIcon; tone: string } {
  if (type === 'plan.day_alert' || type === 'plan.alert_starts') return { Icon: AlertTriangle, tone: 'bg-amber-50 text-amber-700' }
  if (type === 'plan.day_note') return { Icon: StickyNote, tone: 'bg-amber-50 text-amber-700' }
  if (type === 'plan.removed') return { Icon: CalendarX, tone: 'bg-slate-100 text-ink-600' }
  if (type.startsWith('plan.')) return { Icon: CalendarDays, tone: 'bg-blue-50 text-blue-700' }
  if (type === 'hours.missing') return { Icon: Clock, tone: 'bg-red-50 text-red-700' }
  if (type === 'order.mention') return { Icon: AtSign, tone: 'bg-brand-50 text-brand-700' }
  if (type === 'task.comment') return { Icon: MessageSquare, tone: 'bg-violet-50 text-violet-700' }
  if (type.startsWith('task.')) return { Icon: CheckSquare, tone: 'bg-violet-50 text-violet-700' }
  if (type.startsWith('preparation.')) return { Icon: Package, tone: 'bg-emerald-50 text-emerald-700' }
  if (type.startsWith('absence.')) return { Icon: CalendarOff, tone: 'bg-sky-50 text-sky-700' }
  if (type.startsWith('protocol.')) return { Icon: FileCheck, tone: 'bg-emerald-50 text-emerald-700' }
  if (type === 'asset.deadline') return { Icon: CalendarClock, tone: 'bg-amber-50 text-amber-700' }
  return { Icon: Bell, tone: 'bg-slate-100 text-ink-600' }
}

/** Relativní čas česky: „právě teď", „před 5 min", „před 2 h", „včera 14:30", jinak datum a čas. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  const diffSec = Math.round((now.getTime() - d.getTime()) / 1000)
  if (diffSec < 60) return 'právě teď'
  const min = Math.floor(diffSec / 60)
  if (min < 60) return `před ${min} min`
  const h = Math.floor(min / 60)
  const sameDay = d.toDateString() === now.toDateString()
  if (h < 24 && sameDay) return `před ${h} h`
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const time = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === yesterday.toDateString()) return `včera ${time}`
  return formatDateTime(d)
}

/** Jedna notifikace v seznamu (panel u zvonečku i stránka /notifications). */
export function NotificationItem({ notification: n, onOpen }: { notification: AppNotification; onOpen: (n: AppNotification) => void }) {
  const unread = n.read_at === null
  const { Icon, tone } = iconFor(n.type)
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className={clsx(
        'flex w-full min-h-[56px] items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
        unread ? 'bg-brand-50/60 hover:bg-brand-50' : 'hover:bg-slate-50',
      )}
    >
      <span className={clsx('mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full', tone)} aria-hidden>
        <Icon size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={clsx('block text-sm leading-snug', unread ? 'font-semibold text-ink-900' : 'font-medium text-ink-800')}>{n.title}</span>
        {n.body && <span className="mt-0.5 line-clamp-2 block whitespace-pre-line text-sm text-ink-600">{n.body}</span>}
        <span className="mt-1 block text-xs text-ink-500">
          {relativeTime(n.created_at)}
          {n.actor ? ` · ${n.actor.name}` : ''}
          {unread && <span className="sr-only"> · nepřečteno</span>}
        </span>
      </span>
      {unread && <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-brand-600" aria-hidden />}
    </button>
  )
}
